import { colors } from '@kmns-internal/furikake/tokens'
import {
  Box,
  Divider,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
// date-fns と date-fns/locale/ja が同一 typings に解決されるため import/no-duplicates の
// 誤検知が出る（PeriodRow 等と同様の既知事象）。実際には別モジュールなので抑制する。
import { format, parseISO } from 'date-fns' // eslint-disable-line import/no-duplicates
import ja from 'date-fns/locale/ja' // eslint-disable-line import/no-duplicates
import { DetailReportLink } from 'src/features/monitoringDashboardAlpha/detail/components/DetailReportLink/DetailReportLink'
import {
  DetailStatusCell,
  statusCellBgColor,
} from 'src/features/monitoringDashboardAlpha/detail/components/DetailStatusCell/DetailStatusCell'
import { DashboardReportDetail } from 'src/features/monitoringDashboardAlpha/detail/types'
import { rowsPerPageOptions } from 'src/features/monitoringDashboardAlpha/hooks/useMonitoringFilters'

type Props = {
  details: DashboardReportDetail[]
  count: number
  offset: number
  limit: number
  isLoading: boolean
  onChangePage: (offset: number) => void
  onChangeLimit: (limit: number) => void
}

const headCellSx = {
  fontWeight: 600,
  color: colors.text.secondary,
  whiteSpace: 'nowrap',
} as const

/** ISO 日付を「5/25(月)」形式に整形する */
const formatDate = (iso: string): string =>
  iso ? format(parseISO(iso), 'M/d(E)', { locale: ja }) : ''

/** 記録中（一時保存あり）のステータスか */
const isTemporarilySaved = (status: DashboardReportDetail['status']): boolean =>
  status === 'recording' || status === 'recordingOverdue'

/**
 * 詳細（L2）の明細テーブル（Card 形式・内部にページネーション）
 *
 * レポートへの遷移は「該当レポート」列のリンクで行う（行クリックは無効）。
 */
export const DetailTable = ({
  details,
  count,
  offset,
  limit,
  isLoading,
  onChangePage,
  onChangeLimit,
}: Props) => {
  const page = limit > 0 ? Math.floor(offset / limit) : 0

  return (
    <Paper variant="outlined">
      <TableContainer sx={{ position: 'relative' }}>
        {isLoading && (
          <LinearProgress
            sx={{ position: 'absolute', top: 0, left: 0, right: 0 }}
          />
        )}
        <Table size="small" sx={{ bgcolor: colors.white }}>
          <TableHead>
            <TableRow>
              {/* 左の2列（スケジュール・現場）に幅を多めに割り当てる */}
              <TableCell sx={headCellSx}>スケジュール</TableCell>
              <TableCell sx={headCellSx}>現場</TableCell>
              <TableCell sx={{ ...headCellSx, width: 120 }}>日付</TableCell>
              <TableCell align="center" sx={{ ...headCellSx, width: 120 }}>
                ステータス
              </TableCell>
              <TableCell sx={{ ...headCellSx, width: 220 }}>
                該当レポート
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {details.map(detail => (
              <TableRow key={detail.id}>
                <TableCell sx={{ maxWidth: 360 }}>
                  <Typography
                    variant="body2"
                    noWrap
                    title={detail.scheduleName}
                  >
                    {detail.scheduleName}
                  </Typography>
                </TableCell>
                <TableCell sx={{ maxWidth: 320 }}>
                  <Typography variant="body2" noWrap title={detail.placeName}>
                    {detail.placeName}
                  </Typography>
                </TableCell>
                <TableCell sx={{ width: 120, whiteSpace: 'nowrap' }}>
                  <Typography variant="body2">
                    {formatDate(detail.date)}
                  </Typography>
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ width: 120, bgcolor: statusCellBgColor[detail.status] }}
                >
                  <DetailStatusCell status={detail.status} />
                </TableCell>
                <TableCell sx={{ width: 220 }}>
                  <DetailReportLink
                    reportUuid={detail.reportUuid}
                    isTemporarilySaved={isTemporarilySaved(detail.status)}
                  />
                </TableCell>
              </TableRow>
            ))}
            {!isLoading && details.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Box sx={{ py: 4, textAlign: 'center' }}>
                    <Typography
                      variant="body2"
                      sx={{ color: colors.text.secondary }}
                    >
                      該当するレポートがありません
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Divider />
      <TablePagination
        component="div"
        count={count}
        page={page}
        onPageChange={(_, newPage) => onChangePage(newPage * limit)}
        rowsPerPage={limit}
        onRowsPerPageChange={event =>
          onChangeLimit(parseInt(event.target.value, 10))
        }
        rowsPerPageOptions={rowsPerPageOptions}
        labelRowsPerPage="1ページあたり:"
        labelDisplayedRows={({ from, to, count: total }) =>
          `${from}〜${to} / ${total}件`
        }
        sx={{
          '& .MuiTablePagination-selectLabel': { margin: 0 },
          '& .MuiTablePagination-displayedRows': { margin: 0 },
        }}
      />
    </Paper>
  )
}
